// Stripe payment integration for Mapas KM
// Handles subscription payments and redirects to Stripe Checkout

export interface StripeConfig {
  publicKey?: string
  paymentLinks: {
    iniciante: {
      monthly: string
    }
  }
}

export class StripeService {
  private config: StripeConfig

  constructor() {
    // Initialize Stripe with test configuration
    this.config = {
      paymentLinks: {
        iniciante: {
          monthly: 'https://buy.stripe.com/test_6oUbJ06ilbux3K0aP1gnK00' // Provided sandbox link
        }
      }
    }
  }

  /**
   * Redirect user to Stripe Checkout for subscription
   */
  async redirectToCheckout(planName: string): Promise<void> {
    try {
      // Store subscription context for return handling
      this.storeSubscriptionContext(planName)

      // Create checkout session via backend API
      const appOrigin = 'https://mapakms.com'
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          plan_name: planName,
          is_yearly: false,
          success_url: `${appOrigin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${appOrigin}/payment/error`
        })
      })

      if (!response.ok) {
        // Try to get specific error message from API response
        const txt = await response.text();
        let api: any = {}
        try { 
          api = JSON.parse(txt) 
        } catch {/* noop */}
        
        const err: any = new Error(
          api.error ?? `Erro ${response.status}`
        )
        err.status = response.status
        err.code = api.code
        throw err
      }

      const sessionData = await response.json()
      
      // Redirect to Stripe checkout URL
      window.location.href = sessionData.url
      
    } catch (error) {
      console.error('Stripe checkout error:', error)
      // Re-throw the specific error instead of generic message
      throw error
    }
  }

  /**
   * Store subscription context in localStorage for return handling
   */
  private storeSubscriptionContext(planName: string): void {
    const context = {
      planName,
      isYearly: false,
      timestamp: Date.now(),
      returnUrl: window.location.pathname
    }
    
    localStorage.setItem('stripe_subscription_context', JSON.stringify(context))
  }

  /**
   * Retrieve and clear subscription context
   */
  getAndClearSubscriptionContext(): any {
    const context = localStorage.getItem('stripe_subscription_context')
    if (context) {
      localStorage.removeItem('stripe_subscription_context')
      return JSON.parse(context)
    }
    return null
  }

  /**
   * Check if user is returning from Stripe (based on URL parameters)
   */
  isReturningFromStripe(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has('session_id') || urlParams.has('payment_intent')
  }

  /**
   * Update payment links configuration (for future backend integration)
   */
  updatePaymentLinks(newConfig: Partial<StripeConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }
}

// Singleton instance
export const stripeService = new StripeService()
